import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import Layout from '../../components/Layout';
import StudentExerciseProtection from '../../components/StudentExerciseProtection';
import { studentAPI } from '../../services/api';
import './ExerciseWorkspace.css';

function ExerciseWorkspace({ user, onLogout }) {
  const { exerciseId } = useParams();
  const [exercise, setExercise] = useState(null);
  const [currentTask, setCurrentTask] = useState(null);
  const [files, setFiles] = useState({});
  const [activeFile, setActiveFile] = useState('index.html');
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showHints, setShowHints] = useState(false);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);

  useEffect(() => {
    loadExercise();
  }, [exerciseId]);

  useEffect(() => {
    if (exercise) {
      const taskId = exercise.currentTask || exercise.tasks[0]?.id;
      if (taskId) loadWorkspace(taskId);
    }
  }, [exercise]);

  // Auto-save
  useEffect(() => {
    if (files && Object.keys(files).length > 0 && currentTask) {
      if (autoSaveTimer) clearTimeout(autoSaveTimer);
      
      const timer = setTimeout(() => {
        saveWorkspace();
      }, 3000);
      
      setAutoSaveTimer(timer);
      
      return () => clearTimeout(timer);
    }
  }, [files]);

  const loadExercise = async () => {
    try {
      setLoading(true);
      const response = await studentAPI.getExercise(exerciseId);
      setExercise(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load exercise');
    } finally {
      setLoading(false);
    }
  };

  const loadWorkspace = async (taskId) => {
    try {
      const response = await studentAPI.getWorkspace(exerciseId, taskId);
      setFiles(response.data.files);
      
      const fileNames = Object.keys(response.data.files);
      if (fileNames.length > 0 && !fileNames.includes(activeFile)) {
        setActiveFile(fileNames[0]);
      }
      
      const task = exercise.tasks.find(t => t.id === taskId);
      setCurrentTask(task);
      setTestResults(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load workspace');
    }
  };

  const saveWorkspace = async () => {
    if (!currentTask) return;
    
    try {
      setSaving(true);
      await studentAPI.saveWorkspace(exerciseId, currentTask.id, files);
    } catch (err) {
      console.error('Auto-save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const runTests = async () => {
    if (!currentTask) return;
    
    setTesting(true);
    setError('');
    
    try {
      // Save before testing
      await studentAPI.saveWorkspace(exerciseId, currentTask.id, files);
      
      const response = await studentAPI.runTests(exerciseId, currentTask.id);
      setTestResults(response.data);
      
      if (response.data.taskPassed) {
        // Reload exercise to get updated task list
        await loadExercise();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to run tests');
    } finally {
      setTesting(false);
    }
  };

  const handleFileChange = (filename, value) => {
    setFiles(prev => ({
      ...prev,
      [filename]: value
    }));
  };

  const switchTask = async (taskId) => {
    await saveWorkspace();
    await loadWorkspace(taskId);
  };

  const renderInstructions = (instructions) => {
    const renderInstructionText = (text) => text
      .split(/(<!DOCTYPE\s+html>|<\/?[a-z][^>]*>)/gi)
      .map((part, index) => /^<!DOCTYPE\s+html>$|^<\/?[a-z][^>]*>$/i.test(part)
        ? <code key={index}>{part}</code>
        : part);

    const numberedSteps = instructions
      .trim()
      .split(/(?=^\s*\d+\.\s)/m)
      .filter(Boolean);

    if (numberedSteps.length > 1) {
      return (
        <ol className="instruction-list">
          {numberedSteps.map((step, index) => (
            <li key={index}>{renderInstructionText(step.replace(/^\s*\d+\.\s*/, ''))}</li>
          ))}
        </ol>
      );
    }

    return <div className="instructions-text">{renderInstructionText(instructions)}</div>;
  };

  const getPreviewContent = () => {
    const html = files['index.html'] || '';
    const aboutHtml = files['about.html'] || '';
    const css = files['style.css'] || '';
    const js = files['script.js'] || '';
    
    // Inject navigation handler to simulate multi-page navigation
    const navigationScript = `
      <script>
        // Simulate multi-page navigation within iframe
        let currentPage = 'index';
        const pages = {
          'index': \`${html.replace(/<html[^>]*>|<\/html>|<head[^>]*>|<\/head>|<body[^>]*>|<\/body>/gi, '').replace(/`/g, '\\`')}\`,
          'about': \`${aboutHtml.replace(/<html[^>]*>|<\/html>|<head[^>]*>|<\/head>|<body[^>]*>|<\/body>/gi, '').replace(/`/g, '\\`')}\`
        };
        
        function loadPage(pageName) {
          currentPage = pageName;
          const content = pages[pageName] || pages['index'];
          document.body.innerHTML = content;
          attachLinkHandlers();
        }
        
        function attachLinkHandlers() {
          document.querySelectorAll('a').forEach(link => {
            link.addEventListener('click', (e) => {
              const href = link.getAttribute('href');
              if (href && (href.endsWith('.html') || href === 'index.html' || href === 'about.html')) {
                e.preventDefault();
                const pageName = href.replace('.html', '');
                if (pages[pageName]) {
                  loadPage(pageName);
                }
              }
            });
          });
        }
        
        // Initialize on load
        window.addEventListener('DOMContentLoaded', () => {
          attachLinkHandlers();
        });
      </script>
    `;
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <style>${css}</style>
        </head>
        <body>
          ${html.replace(/<html[^>]*>|<\/html>|<head[^>]*>|<\/head>|<body[^>]*>|<\/body>/gi, '')}
          <script>${js}</script>
          ${navigationScript}
        </body>
      </html>
    `;
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="loading">Loading exercise...</div>
      </Layout>
    );
  }

  if (error && !exercise) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="error-message">{error}</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <StudentExerciseProtection>
      <div className="exercise-workspace">
        <div className="workspace-header">
          <div>
            <Link to="/student/dashboard" className="back-link">← Back to Dashboard</Link>
            <h1>{exercise.exercise.title}</h1>
            {exercise.exercise.description && (
              <p className="workspace-description">{exercise.exercise.description}</p>
            )}
          </div>
          <span className={`save-indicator ${saving ? 'is-saving' : ''}`} aria-live="polite">
            {saving ? 'Saving your work…' : 'Changes save automatically'}
          </span>
        </div>

        <div className="workspace-container">
          {/* Sidebar */}
          <div className="workspace-sidebar">
            <div className="sidebar-heading">
              <h2>Exercise steps</h2>
              <span>{exercise.tasks.filter(task => task.status === 'passed').length}/{exercise.tasks.length} completed</span>
            </div>
            <div
              className="task-progress-track"
              role="progressbar"
              aria-label="Exercise progress"
              aria-valuemin="0"
              aria-valuemax={exercise.tasks.length}
              aria-valuenow={exercise.tasks.filter(task => task.status === 'passed').length}
            >
              <span
                style={{
                  width: `${(exercise.tasks.filter(task => task.status === 'passed').length / exercise.tasks.length) * 100}%`
                }}
              />
            </div>
            <div className="task-list">
              {exercise.tasks.map((task, index) => (
                <button
                  key={task.id}
                  className={`task-item ${task.status} ${currentTask?.id === task.id ? 'active' : ''}`}
                  onClick={() => switchTask(task.id)}
                  aria-label={`Step ${index + 1}: ${task.title} — ${task.status}`}
                  aria-current={currentTask?.id === task.id ? 'step' : undefined}
                >
                  <span className="task-status-icon" aria-hidden="true">
                    {task.status === 'passed' && '✓'}
                    {task.status === 'current' && '▶'}
                    {task.status === 'locked' && '🔒'}
                  </span>
                  <span className="task-copy">
                    <span className="task-step">Step {index + 1}</span>
                    <span className="task-title">{task.title}</span>
                  </span>
                  <span className={`task-status-label ${task.status}`}>
                    {task.status === 'passed' ? 'Done' : task.status === 'current' ? 'In progress' : 'Locked'}
                  </span>
                </button>
              ))}
            </div>

          </div>

          {/* Main workspace */}
          <div className="workspace-main">
            {exercise.quizAvailable && (
              <section className="exercise-complete-card" aria-labelledby="exercise-complete-title">
                <div className="completion-mark" aria-hidden="true">✓</div>
                <span className="completion-eyebrow">Exercise complete</span>
                <h2 id="exercise-complete-title">Odlično odrađeno!</h2>
                <p>
                  Završio/la si sve korake. Spreman/na si provjeriti svoje znanje u završnom kvizu.
                </p>
                <Link
                  to={`/student/exercises/${exerciseId}/quiz`}
                  className="quiz-cta"
                >
                  <span className="quiz-cta-copy">
                    <strong>Pokreni završni kviz</strong>
                    <span>{exercise.exercise.quiz?.questionCount || 10} pitanja · Provjeri svoje znanje</span>
                  </span>
                  <span className="quiz-cta-arrow" aria-hidden="true">→</span>
                </Link>
              </section>
            )}
            {currentTask && (
              <>
                <div className="task-instructions">
                  <div className="task-instructions-header">
                    <div>
                      <span className="eyebrow">Step {exercise.tasks.findIndex(task => task.id === currentTask.id) + 1} of {exercise.tasks.length}</span>
                      <h2>{currentTask.title}</h2>
                    </div>
                    <span className={`current-task-status ${currentTask.status}`}>
                      {currentTask.status === 'passed' ? 'Completed' : 'In progress'}
                    </span>
                  </div>
                  <div className="instruction-content">
                    {renderInstructions(currentTask.instructions)}
                  </div>
                  
                  <div className="task-actions">
                    <button className="btn btn-primary" onClick={runTests} disabled={testing}>
                      {testing ? 'Checking your work…' : 'Check my work'}
                    </button>
                    <button className="btn btn-secondary" onClick={() => setShowHints(!showHints)}>
                      {showHints ? 'Hide tips' : 'Need a hint?'}
                    </button>
                  </div>

                  {showHints && currentTask.hints && (
                    <div className="hints-box">
                      <h4>Hints</h4>
                      {currentTask.hints.map((hint, index) => (
                        <div key={index} className="hint-item">
                          <strong>Hint {index + 1}:</strong> {hint}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="editor-preview-container">
                  {/* Code Editor */}
                  <div className="editor-section">
                    <div className="file-tabs">
                      {Object.keys(files).map(filename => (
                        <button
                          key={filename}
                          className={`file-tab ${activeFile === filename ? 'active' : ''}`}
                          onClick={() => setActiveFile(filename)}
                          aria-pressed={activeFile === filename}
                        >
                          {filename}
                        </button>
                      ))}
                    </div>
                    <Editor
                      height="100%"
                      language={getLanguage(activeFile)}
                      value={files[activeFile] || ''}
                      onChange={(value) => handleFileChange(activeFile, value)}
                      theme="vs-dark"
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        lineNumbers: 'on',
                        automaticLayout: true,
                        readOnly: false
                      }}
                    />
                  </div>

                  {/* Live Preview */}
                  <div className="preview-section">
                    <div className="preview-header">
                      <span>Live preview</span>
                      <span className="preview-hint">Updates as you type</span>
                    </div>
                    <iframe
                      title="preview"
                      srcDoc={getPreviewContent()}
                      sandbox="allow-scripts"
                      className="preview-frame"
                    />
                  </div>
                </div>

                {/* Test Results */}
                {testResults && (
                  <div className={`test-results ${testResults.summary.allPassed ? 'success' : 'failure'}`}>
                    <h3>
                      Step checks: {testResults.summary.passed} / {testResults.summary.total} passed
                    </h3>
                    <div className="test-list">
                      {testResults.results.map((result, index) => (
                        <div key={index} className={`test-item ${result.passed ? 'passed' : 'failed'}`}>
                          <span className="test-icon">{result.passed ? '✓' : '✗'}</span>
                          <div className="test-details">
                            <div className="test-name">{result.name}</div>
                            <div className="test-message">{result.message}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {testResults.summary.allPassed && (
                      <div className="success-message">
                        🎉 Great job! All tests passed. {testResults.nextTask ? 'Moving to next task...' : 'Exercise complete!'}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      </StudentExerciseProtection>
    </Layout>
  );
}

function getLanguage(filename) {
  if (filename.endsWith('.html')) return 'html';
  if (filename.endsWith('.css')) return 'css';
  if (filename.endsWith('.js')) return 'javascript';
  return 'plaintext';
}

export default ExerciseWorkspace;
